import React, {useState, useEffect} from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {useApp} from '../context/AppContext';

const {FiInfo, FiChevronDown, FiChevronUp, FiCalculator, FiBookOpen, FiTarget, FiSave, FiEdit3, FiAlertCircle} = FiIcons;

const SLTWeightage = () => {
  const {state, dispatch, assessmentService} = useApp();
  const [sltData, setSltData] = useState([]);
  const [showCalculationGuide, setShowCalculationGuide] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  // Course SLT Configuration - now editable
  const [courseSLTConfig, setCourseSLTConfig] = useState({
    creditHours: state.activeCourse?.credit_hours || state.activeCourse?.creditHours || 3,
    sltPerCredit: 40, // National standard
    totalCourseSLT: 120, // Now directly editable
    totalTeachingSLT: 67, // ~56% of total
    totalAssessmentSLT: 53, // ~44% of total
    f2fPercentage: 34, // 34% of total SLT
    independentPercentage: 66 // 66% of total SLT
  });

  // Teaching SLT by CLO - now editable
  const [teachingSLTByCLO, setTeachingSLTByCLO] = useState({
    CLO1: 18,
    CLO2: 16,
    CLO3: 22
  });

  // Directly configurable F2F and Independent Learning breakdown
  const [breakdownConfig, setBreakdownConfig] = useState({
    f2fTeaching: 23, // ~34% of teaching SLT
    f2fAssessment: 18, // ~34% of assessment SLT
    independentTeaching: 44, // ~66% of teaching SLT
    independentAssessment: 35, // ~66% of assessment SLT
  });

  // Load saved configuration when the component mounts or active course changes
  useEffect(() => {
    if (state.activeCourse) {
      if (state.sltConfiguration.courseSLTConfig) {
        // If there's a saved configuration in the state, use it
        setCourseSLTConfig(state.sltConfiguration.courseSLTConfig);
        setTeachingSLTByCLO(state.sltConfiguration.teachingSLTByCLO);
        setBreakdownConfig(state.sltConfiguration.breakdownConfig);
      } else {
        // Otherwise, load the default configuration based on course credit hours
        loadSLTConfiguration();
      }
    }
    
    if (state.assessmentPlan.length > 0) {
      calculateSLTData();
    } else {
      // Use sample data for demonstration
      setSltData([
        {
          clo: 'CLO1',
          sltTeaching: 18,
          percentageA: 32.1,
          weightage: 35,
          sltAssessment: 16,
          percentageB: 35,
          difference: -2.9,
          f2fTeaching: 8,
          f2fAssessment: 2,
          independentTeaching: 10,
          independentAssessment: 14
        },
        {
          clo: 'CLO2',
          sltTeaching: 16,
          percentageA: 28.6,
          weightage: 30,
          sltAssessment: 14,
          percentageB: 30,
          difference: -1.4,
          f2fTeaching: 7,
          f2fAssessment: 2,
          independentTeaching: 9,
          independentAssessment: 12
        },
        {
          clo: 'CLO3',
          sltTeaching: 22,
          percentageA: 39.3,
          weightage: 35,
          sltAssessment: 14,
          percentageB: 35,
          difference: 4.3,
          f2fTeaching: 9,
          f2fAssessment: 2,
          independentTeaching: 13,
          independentAssessment: 12
        }
      ]);
    }
  }, [state.assessmentPlan, state.activeCourse, state.sltConfiguration]);

  useEffect(() => {
    // Recalculate when configuration changes
    if (state.assessmentPlan.length > 0 || Object.keys(teachingSLTByCLO).length > 0) {
      calculateSLTData();
    }
  }, [courseSLTConfig, teachingSLTByCLO, breakdownConfig]);

  // Update breakdown config when total SLT changes to keep initial proportions
  useEffect(() => {
    // Initialize F2F and Independent Learning values based on percentages
    if (!isEditing) {
      initializeBreakdownValues();
    }
  }, [courseSLTConfig.totalTeachingSLT, courseSLTConfig.totalAssessmentSLT]);

  const initializeBreakdownValues = () => {
    // Only update if not manually edited by user
    const f2fTeaching = Math.round(courseSLTConfig.totalTeachingSLT * courseSLTConfig.f2fPercentage / 100);
    const f2fAssessment = Math.round(courseSLTConfig.totalAssessmentSLT * courseSLTConfig.f2fPercentage / 100);
    const independentTeaching = courseSLTConfig.totalTeachingSLT - f2fTeaching;
    const independentAssessment = courseSLTConfig.totalAssessmentSLT - f2fAssessment;
    
    setBreakdownConfig({
      f2fTeaching,
      f2fAssessment,
      independentTeaching,
      independentAssessment
    });
  };

  const loadSLTConfiguration = async () => {
    // Calculate based on course credit hours
    const creditHours = state.activeCourse?.credit_hours || state.activeCourse?.creditHours || 3;
    const recommendedTotalSLT = creditHours * 40; // 40 hours per credit is the standard
    const teachingSLT = Math.round(recommendedTotalSLT * 0.56); // 56% for teaching
    const assessmentSLT = recommendedTotalSLT - teachingSLT; // Remaining for assessment
    
    // Set the course SLT configuration
    setCourseSLTConfig({
      creditHours,
      sltPerCredit: 40,
      totalCourseSLT: recommendedTotalSLT,
      totalTeachingSLT: teachingSLT,
      totalAssessmentSLT: assessmentSLT,
      f2fPercentage: 34,
      independentPercentage: 66
    });

    // Initialize teaching SLT based on available CLOs
    if (state.cloStatements && Object.keys(state.cloStatements).length > 0) {
      const cloKeys = Object.keys(state.cloStatements);
      const equalShare = Math.round(teachingSLT / cloKeys.length);
      const newTeachingSLT = {};
      cloKeys.forEach(clo => {
        newTeachingSLT[clo] = equalShare;
      });
      setTeachingSLTByCLO(newTeachingSLT);
    }

    // Initialize breakdown configuration
    const f2fTeaching = Math.round(teachingSLT * 0.34);
    const f2fAssessment = Math.round(assessmentSLT * 0.34);
    const independentTeaching = teachingSLT - f2fTeaching;
    const independentAssessment = assessmentSLT - f2fAssessment;
    
    setBreakdownConfig({
      f2fTeaching,
      f2fAssessment,
      independentTeaching,
      independentAssessment
    });
  };

  const calculateSLTData = () => {
    // Get CLOs from state or use default
    const availableCLOs = state.cloStatements && Object.keys(state.cloStatements).length > 0 
      ? Object.keys(state.cloStatements) 
      : ['CLO1', 'CLO2', 'CLO3'];

    // Group assessments by CLO
    const groupedByCLO = state.assessmentPlan.reduce((acc, assessment) => {
      if (!acc[assessment.clo]) {
        acc[assessment.clo] = {
          clo: assessment.clo,
          sltTeaching: teachingSLTByCLO[assessment.clo] || 0,
          weightage: 0,
          sltAssessment: 0,
          assessments: []
        };
      }
      acc[assessment.clo].weightage += assessment.marks;
      acc[assessment.clo].sltAssessment += assessment.sltCI;
      acc[assessment.clo].assessments.push(assessment);
      return acc;
    }, {});

    // Ensure all available CLOs are included
    availableCLOs.forEach(clo => {
      if (!groupedByCLO[clo]) {
        groupedByCLO[clo] = {
          clo,
          sltTeaching: teachingSLTByCLO[clo] || 0,
          weightage: 0,
          sltAssessment: 0,
          assessments: []
        };
      }
    });

    const sltArray = Object.values(groupedByCLO).map(item => {
      // Calculate % (A) - Teaching SLT percentage
      const percentageA = courseSLTConfig.totalTeachingSLT > 0 
        ? (item.sltTeaching / courseSLTConfig.totalTeachingSLT) * 100 
        : 0;

      // % (B) is same as weightage (assessment marks percentage)
      const percentageB = item.weightage;

      // Calculate A-B difference
      const difference = percentageA - percentageB;

      // Calculate F2F and Independent Learning proportional to the CLO's share
      const teachingRatio = courseSLTConfig.totalTeachingSLT > 0 
        ? item.sltTeaching / courseSLTConfig.totalTeachingSLT 
        : 0;
        
      const assessmentRatio = courseSLTConfig.totalAssessmentSLT > 0 
        ? item.sltAssessment / courseSLTConfig.totalAssessmentSLT 
        : 0;

      // Apply the ratios to the breakdown configuration
      const f2fTeaching = Math.round(breakdownConfig.f2fTeaching * teachingRatio);
      const independentTeaching = Math.round(breakdownConfig.independentTeaching * teachingRatio);
      const f2fAssessment = Math.round(breakdownConfig.f2fAssessment * assessmentRatio);
      const independentAssessment = Math.round(breakdownConfig.independentAssessment * assessmentRatio);

      return {
        ...item,
        percentageA: parseFloat(percentageA.toFixed(1)),
        percentageB: percentageB,
        difference: parseFloat(difference.toFixed(1)),
        f2fTeaching,
        f2fAssessment,
        independentTeaching,
        independentAssessment
      };
    });

    setSltData(sltArray);
  };

  const handleConfigChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    
    if (field === 'creditHours' || field === 'sltPerCredit') {
      // Update recommended SLT based on credit hours and SLT per credit
      const recommendedTotalSLT = (field === 'creditHours' ? numValue : courseSLTConfig.creditHours) * 
                                 (field === 'sltPerCredit' ? numValue : courseSLTConfig.sltPerCredit);
      
      setCourseSLTConfig(prev => ({
        ...prev,
        [field]: numValue,
        // Don't automatically update totalCourseSLT to preserve user adjustments
      }));
    } else if (field === 'totalCourseSLT') {
      // When total SLT is directly changed, update teaching and assessment distribution
      const teachingSLT = Math.round(numValue * 0.56); // 56% for teaching
      const assessmentSLT = numValue - teachingSLT; // Remaining for assessment
      
      setCourseSLTConfig(prev => ({
        ...prev,
        totalCourseSLT: numValue,
        totalTeachingSLT: teachingSLT,
        totalAssessmentSLT: assessmentSLT
      }));
    } else if (field === 'totalTeachingSLT') {
      // When teaching SLT is changed, update assessment SLT accordingly
      const assessmentSLT = courseSLTConfig.totalCourseSLT - numValue;
      
      setCourseSLTConfig(prev => ({
        ...prev,
        totalTeachingSLT: numValue,
        totalAssessmentSLT: assessmentSLT
      }));
    } else if (field === 'totalAssessmentSLT') {
      // When assessment SLT is changed, update teaching SLT accordingly
      const teachingSLT = courseSLTConfig.totalCourseSLT - numValue;
      
      setCourseSLTConfig(prev => ({
        ...prev,
        totalAssessmentSLT: numValue,
        totalTeachingSLT: teachingSLT
      }));
    } else {
      setCourseSLTConfig(prev => ({
        ...prev,
        [field]: numValue
      }));
    }
  };

  const handleTeachingSLTChange = (clo, value) => {
    const numValue = parseFloat(value) || 0;
    setTeachingSLTByCLO(prev => ({
      ...prev,
      [clo]: numValue
    }));
  };

  // Handle direct changes to F2F and Independent Learning breakdown
  const handleBreakdownChange = (field, value) => {
    const numValue = parseInt(value) || 0;
    
    let updatedConfig = { ...breakdownConfig };

    // Update the specified field
    updatedConfig[field] = numValue;
    
    // Handle dependencies to maintain totals
    if (field === 'f2fTeaching' || field === 'independentTeaching') {
      // Ensure teaching total matches configured total
      if (field === 'f2fTeaching') {
        updatedConfig.independentTeaching = courseSLTConfig.totalTeachingSLT - numValue;
      } else {
        updatedConfig.f2fTeaching = courseSLTConfig.totalTeachingSLT - numValue;
      }
    }
    
    if (field === 'f2fAssessment' || field === 'independentAssessment') {
      // Ensure assessment total matches configured total
      if (field === 'f2fAssessment') {
        updatedConfig.independentAssessment = courseSLTConfig.totalAssessmentSLT - numValue;
      } else {
        updatedConfig.f2fAssessment = courseSLTConfig.totalAssessmentSLT - numValue;
      }
    }
    
    // Update the breakdown configuration
    setBreakdownConfig(updatedConfig);
  };

  const saveSLTConfiguration = async () => {
    if (!state.activeCourse) {
      alert('Please select a course first');
      return;
    }

    setLoading(true);
    try {
      // Save SLT configuration to database
      const { data, error } = await assessmentService.saveSLTConfiguration(
        state.activeCourse.id,
        courseSLTConfig,
        teachingSLTByCLO,
        breakdownConfig
      );

      if (error) throw new Error(error);

      // Update application state with the saved configuration
      dispatch({
        type: 'SET_SLT_CONFIGURATION',
        payload: {
          courseSLTConfig,
          teachingSLTByCLO,
          breakdownConfig
        }
      });

      alert('SLT configuration saved successfully!');
      setIsEditing(false);
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      alert('Error saving SLT configuration: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const totalTeachingSLT = sltData.reduce((sum, item) => sum + item.sltTeaching, 0);
  const totalAssessmentSLT = sltData.reduce((sum, item) => sum + item.sltAssessment, 0);
  const totalCourseSLT = totalTeachingSLT + totalAssessmentSLT;

  // Calculate recommended SLT based on credit hours
  const recommendedSLT = courseSLTConfig.creditHours * courseSLTConfig.sltPerCredit;
  const sltDifference = courseSLTConfig.totalCourseSLT - recommendedSLT;
  const sltDifferencePercent = recommendedSLT > 0 ? Math.round((sltDifference / recommendedSLT) * 100) : 0;

  // Check if breakdown totals match configured totals
  const teachingBreakdownTotal = breakdownConfig.f2fTeaching + breakdownConfig.independentTeaching;
  const assessmentBreakdownTotal = breakdownConfig.f2fAssessment + breakdownConfig.independentAssessment;
  const teachingBreakdownMismatch = teachingBreakdownTotal !== courseSLTConfig.totalTeachingSLT;
  const assessmentBreakdownMismatch = assessmentBreakdownTotal !== courseSLTConfig.totalAssessmentSLT;

  const getAlignmentStatus = (difference) => {
    const abs = Math.abs(difference);
    if (abs <= 5) return {status: 'excellent', color: 'text-green-600 bg-green-50'};
    if (abs <= 10) return {status: 'good', color: 'text-yellow-600 bg-yellow-50'};
    return {status: 'needs-adjustment', color: 'text-red-600 bg-red-50'};
  };

  if (!state.activeCourse) {
    return (
      <motion.div
        initial={{opacity: 0, y: 20}}
        animate={{opacity: 1, y: 0}}
        className="flex items-center justify-center h-64"
      >
        <div className="text-center">
          <SafeIcon icon={FiBookOpen} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Course Selected</h3>
          <p className="text-gray-600">Please select a course to configure SLT and weightage.</p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{opacity: 0, y: 20}}
      animate={{opacity: 1, y: 0}}
      className="space-y-6"
    >
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Table 2: SLT & Assessment Marks Weightage</h2>
          <div className="flex space-x-2">
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <SafeIcon icon={FiEdit3} className="w-4 h-4" />
              <span>{isEditing ? 'View Mode' : 'Edit SLT'}</span>
            </button>
            <button
              onClick={() => setShowBreakdown(!showBreakdown)}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <SafeIcon icon={FiTarget} className="w-4 h-4" />
              <span>F2F/Independent</span>
              <SafeIcon icon={showBreakdown ? FiChevronUp : FiChevronDown} className="w-4 h-4" />
            </button>
            <button
              onClick={() => setShowCalculationGuide(!showCalculationGuide)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <SafeIcon icon={FiInfo} className="w-4 h-4" />
              <span>Calculation Guide</span>
              <SafeIcon icon={showCalculationGuide ? FiChevronUp : FiChevronDown} className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Course SLT Configuration */}
        <div className="mb-8 p-6 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-blue-900">Course SLT Configuration</h3>
            {isEditing && (
              <button
                onClick={saveSLTConfiguration}
                disabled={loading}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <SafeIcon icon={FiSave} className="w-4 h-4" />
                <span>{loading ? 'Saving...' : 'Save Configuration'}</span>
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded border border-blue-200">
              <label className="block text-sm font-medium text-blue-700 mb-2">Credit Hours</label>
              {isEditing ? (
                <input
                  type="number"
                  value={courseSLTConfig.creditHours}
                  onChange={(e) => handleConfigChange('creditHours', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  min="1"
                  max="6"
                />
              ) : (
                <p className="text-xl font-bold text-blue-900">{courseSLTConfig.creditHours}</p>
              )}
            </div>

            <div className="bg-white p-4 rounded border border-blue-200">
              <label className="block text-sm font-medium text-blue-700 mb-2">SLT per Credit</label>
              {isEditing ? (
                <input
                  type="number"
                  value={courseSLTConfig.sltPerCredit}
                  onChange={(e) => handleConfigChange('sltPerCredit', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  min="30"
                  max="50"
                />
              ) : (
                <p className="text-xl font-bold text-blue-900">{courseSLTConfig.sltPerCredit} hrs</p>
              )}
              <p className="text-xs text-blue-600 mt-1">Standard practice: 40hrs/credit</p>
            </div>

            <div className="bg-white p-4 rounded border border-blue-200">
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-blue-700">Total Course SLT</label>
                {Math.abs(sltDifferencePercent) > 10 && (
                  <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full flex items-center">
                    <SafeIcon icon={FiAlertCircle} className="w-3 h-3 mr-1" />
                    {sltDifferencePercent > 0 ? 'Above' : 'Below'} recommended
                  </span>
                )}
              </div>
              {isEditing ? (
                <input
                  type="number"
                  value={courseSLTConfig.totalCourseSLT}
                  onChange={(e) => handleConfigChange('totalCourseSLT', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  min="30"
                />
              ) : (
                <p className="text-xl font-bold text-blue-900">{courseSLTConfig.totalCourseSLT} hrs</p>
              )}
              <div className="flex justify-between items-center mt-1">
                <p className="text-xs text-blue-600">
                  Recommended: {recommendedSLT} hrs
                </p>
                <p className={`text-xs ${sltDifference > 0 ? 'text-green-600' : sltDifference < 0 ? 'text-red-600' : 'text-blue-600'}`}>
                  {sltDifference > 0 ? '+' : ''}{sltDifference} hrs
                </p>
              </div>
            </div>

            <div className="bg-white p-4 rounded border border-blue-200">
              <p className="text-blue-700 font-medium">SLT Distribution</p>
              <div className="space-y-3 mt-2">
                <div>
                  <div className="flex justify-between text-sm">
                    <span>Teaching:</span>
                    {isEditing ? (
                      <input
                        type="number"
                        value={courseSLTConfig.totalTeachingSLT}
                        onChange={(e) => handleConfigChange('totalTeachingSLT', e.target.value)}
                        className="w-20 px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        min="0"
                      />
                    ) : (
                      <span className="font-bold">{courseSLTConfig.totalTeachingSLT} hrs</span>
                    )}
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                    <div 
                      className="h-2 rounded-full bg-blue-600" 
                      style={{width: `${(courseSLTConfig.totalTeachingSLT / courseSLTConfig.totalCourseSLT) * 100}%`}}
                    />
                  </div>
                  <p className="text-xs text-right text-blue-600">
                    {courseSLTConfig.totalCourseSLT > 0 
                      ? Math.round((courseSLTConfig.totalTeachingSLT / courseSLTConfig.totalCourseSLT) * 100) 
                      : 0}%
                  </p>
                </div>
                <div>
                  <div className="flex justify-between text-sm">
                    <span>Assessment:</span>
                    {isEditing ? (
                      <input
                        type="number"
                        value={courseSLTConfig.totalAssessmentSLT}
                        onChange={(e) => handleConfigChange('totalAssessmentSLT', e.target.value)}
                        className="w-20 px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        min="0"
                      />
                    ) : (
                      <span className="font-bold">{courseSLTConfig.totalAssessmentSLT} hrs</span>
                    )}
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                    <div 
                      className="h-2 rounded-full bg-green-600" 
                      style={{width: `${(courseSLTConfig.totalAssessmentSLT / courseSLTConfig.totalCourseSLT) * 100}%`}}
                    />
                  </div>
                  <p className="text-xs text-right text-green-600">
                    {courseSLTConfig.totalCourseSLT > 0 
                      ? Math.round((courseSLTConfig.totalAssessmentSLT / courseSLTConfig.totalCourseSLT) * 100) 
                      : 0}%
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Direct F2F vs Independent Learning Configuration */}
          {isEditing && (
            <div className="mt-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <h4 className="font-semibold text-indigo-900 mb-4">F2F vs Independent Learning Configuration</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Teaching SLT Breakdown */}
                <div className="bg-white p-4 rounded-lg border border-indigo-200">
                  <h5 className="font-semibold text-indigo-800 mb-3">Teaching SLT Breakdown ({courseSLTConfig.totalTeachingSLT} hrs)</h5>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-indigo-700 mb-2">F2F Teaching</label>
                      <div className="flex items-center space-x-3">
                        <input 
                          type="number" 
                          value={breakdownConfig.f2fTeaching}
                          onChange={(e) => handleBreakdownChange('f2fTeaching', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          min="0"
                          max={courseSLTConfig.totalTeachingSLT}
                        />
                        <span className="text-sm text-indigo-600">
                          {courseSLTConfig.totalTeachingSLT > 0 
                            ? Math.round((breakdownConfig.f2fTeaching / courseSLTConfig.totalTeachingSLT) * 100)
                            : 0}%
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-indigo-700 mb-2">Independent Teaching</label>
                      <div className="flex items-center space-x-3">
                        <input 
                          type="number" 
                          value={breakdownConfig.independentTeaching}
                          onChange={(e) => handleBreakdownChange('independentTeaching', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          min="0"
                          max={courseSLTConfig.totalTeachingSLT}
                        />
                        <span className="text-sm text-indigo-600">
                          {courseSLTConfig.totalTeachingSLT > 0 
                            ? Math.round((breakdownConfig.independentTeaching / courseSLTConfig.totalTeachingSLT) * 100)
                            : 0}%
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {teachingBreakdownMismatch && (
                    <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                      <SafeIcon icon={FiAlertCircle} className="inline-block w-4 h-4 mr-1" />
                      Total does not match Teaching SLT ({teachingBreakdownTotal} ≠ {courseSLTConfig.totalTeachingSLT})
                    </div>
                  )}
                </div>

                {/* Assessment SLT Breakdown */}
                <div className="bg-white p-4 rounded-lg border border-indigo-200">
                  <h5 className="font-semibold text-indigo-800 mb-3">Assessment SLT Breakdown ({courseSLTConfig.totalAssessmentSLT} hrs)</h5>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-indigo-700 mb-2">F2F Assessment</label>
                      <div className="flex items-center space-x-3">
                        <input 
                          type="number" 
                          value={breakdownConfig.f2fAssessment}
                          onChange={(e) => handleBreakdownChange('f2fAssessment', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          min="0"
                          max={courseSLTConfig.totalAssessmentSLT}
                        />
                        <span className="text-sm text-indigo-600">
                          {courseSLTConfig.totalAssessmentSLT > 0 
                            ? Math.round((breakdownConfig.f2fAssessment / courseSLTConfig.totalAssessmentSLT) * 100)
                            : 0}%
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-indigo-700 mb-2">Independent Assessment</label>
                      <div className="flex items-center space-x-3">
                        <input 
                          type="number" 
                          value={breakdownConfig.independentAssessment}
                          onChange={(e) => handleBreakdownChange('independentAssessment', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          min="0"
                          max={courseSLTConfig.totalAssessmentSLT}
                        />
                        <span className="text-sm text-indigo-600">
                          {courseSLTConfig.totalAssessmentSLT > 0 
                            ? Math.round((breakdownConfig.independentAssessment / courseSLTConfig.totalAssessmentSLT) * 100)
                            : 0}%
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {assessmentBreakdownMismatch && (
                    <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                      <SafeIcon icon={FiAlertCircle} className="inline-block w-4 h-4 mr-1" />
                      Total does not match Assessment SLT ({assessmentBreakdownTotal} ≠ {courseSLTConfig.totalAssessmentSLT})
                    </div>
                  )}
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                <div className="bg-white p-3 rounded-lg border border-indigo-200">
                  <h5 className="font-medium text-indigo-800 text-sm">Total F2F Hours</h5>
                  <p className="text-lg font-semibold text-indigo-900">
                    {breakdownConfig.f2fTeaching + breakdownConfig.f2fAssessment} hrs
                  </p>
                  <p className="text-xs text-indigo-600">
                    {Math.round(((breakdownConfig.f2fTeaching + breakdownConfig.f2fAssessment) / courseSLTConfig.totalCourseSLT) * 100)}% of total
                  </p>
                </div>
                <div className="bg-white p-3 rounded-lg border border-indigo-200">
                  <h5 className="font-medium text-indigo-800 text-sm">Total Independent Hours</h5>
                  <p className="text-lg font-semibold text-indigo-900">
                    {breakdownConfig.independentTeaching + breakdownConfig.independentAssessment} hrs
                  </p>
                  <p className="text-xs text-indigo-600">
                    {Math.round(((breakdownConfig.independentTeaching + breakdownConfig.independentAssessment) / courseSLTConfig.totalCourseSLT) * 100)}% of total
                  </p>
                </div>
                <div className="bg-white p-3 rounded-lg border border-indigo-200">
                  <h5 className="font-medium text-indigo-800 text-sm">F2F Teaching Ratio</h5>
                  <p className="text-lg font-semibold text-indigo-900">
                    {courseSLTConfig.totalTeachingSLT > 0 
                      ? Math.round((breakdownConfig.f2fTeaching / courseSLTConfig.totalTeachingSLT) * 100)
                      : 0}%
                  </p>
                </div>
                <div className="bg-white p-3 rounded-lg border border-indigo-200">
                  <h5 className="font-medium text-indigo-800 text-sm">F2F Assessment Ratio</h5>
                  <p className="text-lg font-semibold text-indigo-900">
                    {courseSLTConfig.totalAssessmentSLT > 0 
                      ? Math.round((breakdownConfig.f2fAssessment / courseSLTConfig.totalAssessmentSLT) * 100)
                      : 0}%
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Teaching SLT by CLO Configuration */}
        {isEditing && (
          <div className="mb-8 p-6 bg-green-50 border border-green-200 rounded-lg">
            <h3 className="font-semibold text-green-900 mb-4">Teaching SLT Distribution by CLO</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(teachingSLTByCLO).map(([clo, hours]) => (
                <div key={clo} className="bg-white p-4 rounded border border-green-200">
                  <label className="block text-sm font-medium text-green-700 mb-2">{clo}</label>
                  <input
                    type="number"
                    value={hours}
                    onChange={(e) => handleTeachingSLTChange(clo, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    min="0"
                  />
                  <p className="text-xs text-green-600 mt-1">
                    {courseSLTConfig.totalTeachingSLT > 0 
                      ? ((hours / courseSLTConfig.totalTeachingSLT) * 100).toFixed(1)
                      : 0}% of teaching SLT
                  </p>
                </div>
              ))}
              <div className="bg-white p-4 rounded border border-green-200">
                <p className="text-sm font-medium text-green-700 mb-2">Total Allocated</p>
                <p className="text-xl font-bold text-green-900">
                  {Object.values(teachingSLTByCLO).reduce((sum, hours) => sum + hours, 0)} hrs
                </p>
                <p className="text-xs text-green-600 mt-1">
                  Target: {courseSLTConfig.totalTeachingSLT} hrs
                </p>
              </div>
            </div>
            {Math.abs(Object.values(teachingSLTByCLO).reduce((sum, hours) => sum + hours, 0) - courseSLTConfig.totalTeachingSLT) > 0 && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-800 text-sm">
                  <strong>Warning:</strong> Total allocated teaching SLT ({Object.values(teachingSLTByCLO).reduce((sum, hours) => sum + hours, 0)} hrs) 
                  does not match target ({courseSLTConfig.totalTeachingSLT} hrs). 
                  Adjust the distribution to ensure accurate calculations.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Calculation Guide Section */}
        {showCalculationGuide && (
          <motion.div
            initial={{opacity: 0, height: 0}}
            animate={{opacity: 1, height: 'auto'}}
            className="mb-8 p-6 bg-blue-50 border border-blue-200 rounded-lg"
          >
            <div className="flex items-center space-x-2 mb-4">
              <SafeIcon icon={FiCalculator} className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-blue-900">Revised Calculation Framework with Expanded Context</h3>
            </div>

            {/* Key Definitions */}
            <div className="mb-6">
              <h4 className="font-semibold text-blue-900 mb-3">Key Definitions</h4>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
                <div className="bg-white p-4 rounded-lg border border-blue-200">
                  <h5 className="font-semibold text-blue-900 mb-2">Continuous Assessment</h5>
                  <p className="text-blue-800 mb-2">Data collection throughout a course to monitor learning, improve teaching, and adjust curriculum design. Includes:</p>
                  <ul className="text-blue-800 list-disc list-inside space-y-1">
                    <li>Alternative assessments, labs, workshops, midterms, quizzes</li>
                    <li>Ensures alignment between course delivery and programme learning outcomes (PLOs)</li>
                  </ul>
                </div>

                <div className="bg-white p-4 rounded-lg border border-blue-200">
                  <h5 className="font-semibold text-blue-900 mb-2">Formative Assessment</h5>
                  <p className="text-blue-800">Low-stakes, feedback-focused activities (e.g., peer reviews, drafts, ungraded quizzes) to guide improvement during learning.</p>
                  <p className="text-blue-700 text-xs mt-2">Aligns with "assessment for learning" (AS/FOR learning)</p>
                </div>

                <div className="bg-white p-4 rounded-lg border border-blue-200">
                  <h5 className="font-semibold text-blue-900 mb-2">Weightage</h5>
                  <p className="text-blue-800 mb-2">The percentage of marks allocated to a CLO, reflecting its importance. Calculated as:</p>
                  <div className="bg-blue-100 p-2 rounded text-blue-900 font-mono text-xs">
                    Weightage = (Teaching Hours for CLO / Total Teaching Hours) × 100
                  </div>
                  <p className="text-blue-700 text-xs mt-2">Must align with assessment task distribution</p>
                </div>

                <div className="bg-white p-4 rounded-lg border border-blue-200">
                  <h5 className="font-semibold text-blue-900 mb-2">Student Learning Time (SLT)</h5>
                  <p className="text-blue-800 mb-2">Total hours for guided (F2F) and independent learning to achieve CLOs.</p>
                  <div className="bg-blue-100 p-2 rounded text-blue-900 font-mono text-xs">
                    Total SLT = Credit Hours × National Standard (e.g., 1 credit = 40 hours)
                  </div>
                  <p className="text-blue-700 text-xs mt-2">Example: 3-credit course = 120 SLT hours; 56 teaching + 64 assessment</p>
                </div>

                <div className="bg-white p-4 rounded-lg border border-blue-200">
                  <h5 className="font-semibold text-blue-900 mb-2">Constructive Alignment</h5>
                  <p className="text-blue-800">Teaching/learning activities and assessments must activate the same verbs as the CLOs</p>
                  <p className="text-blue-700 text-xs mt-2">Example: "analyze" in CLO → "analyze" in exam</p>
                </div>
              </div>
            </div>

            {/* Calculation Steps */}
            <div className="mb-6">
              <h4 className="font-semibold text-blue-900 mb-3">Revised SLT Calculation Framework</h4>
              
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-lg border border-blue-200">
                  <h5 className="font-semibold text-blue-900 mb-2">Step 1: Allocate SLT by Category</h5>
                  <ul className="text-blue-800 space-y-1 text-sm">
                    <li>• Total SLT = Teaching SLT (guided) + Assessment SLT (independent)</li>
                    <li>• Example: 100 SLT hours = 56 (teaching) + 44 (assessment)</li>
                    <li>• <strong>Weekly SLT Scheduling:</strong> Lecturer distributes teaching SLT across 14 weeks (e.g., 4 hours/week)</li>
                    <li>• Assessment SLT is distributed by task (e.g., Assessment 1 due Week 3 = 8 hours)</li>
                  </ul>
                </div>

                <div className="bg-white p-4 rounded-lg border border-blue-200">
                  <h5 className="font-semibold text-blue-900 mb-2">Step 2: Map SLT to CLOs</h5>
                  <ul className="text-blue-800 space-y-1 text-sm">
                    <li>• <strong>Teaching SLT per CLO:</strong> From configuration above (editable)</li>
                    <li>• <strong>Assessment SLT per CLO:</strong> Sum of SLT for mapped assessments</li>
                  </ul>
                </div>

                <div className="bg-white p-4 rounded-lg border border-blue-200">
                  <h5 className="font-semibold text-blue-900 mb-2">Step 3: Calculate Proportions</h5>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border border-gray-300">
                      <thead>
                        <tr className="bg-blue-100">
                          <th className="border border-gray-300 px-2 py-1 text-left">Column</th>
                          <th className="border border-gray-300 px-2 py-1 text-left">Formula</th>
                          <th className="border border-gray-300 px-2 py-1 text-left">Example (CLO1)</th>
                        </tr>
                      </thead>
                      <tbody className="text-blue-800">
                        <tr>
                          <td className="border border-gray-300 px-2 py-1">SLT (Total)</td>
                          <td className="border border-gray-300 px-2 py-1">Teaching SLT for CLO (from configuration)</td>
                          <td className="border border-gray-300 px-2 py-1">18 hours</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 px-2 py-1">% (A)</td>
                          <td className="border border-gray-300 px-2 py-1">(Teaching SLT for CLO / Total Teaching SLT) × 100</td>
                          <td className="border border-gray-300 px-2 py-1">(18/56) × 100 = 32.1%</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 px-2 py-1">Weightage</td>
                          <td className="border border-gray-300 px-2 py-1">Sum of marks for assessments mapped to CLO</td>
                          <td className="border border-gray-300 px-2 py-1">15% + 20% = 35%</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 px-2 py-1">SLT Assessment</td>
                          <td className="border border-gray-300 px-2 py-1">Sum of SLT for mapped assessments</td>
                          <td className="border border-gray-300 px-2 py-1">8 + 8 = 16 hours</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 px-2 py-1">% (B)</td>
                          <td className="border border-gray-300 px-2 py-1">Same as Weightage</td>
                          <td className="border border-gray-300 px-2 py-1">35%</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 px-2 py-1">A-B %</td>
                          <td className="border border-gray-300 px-2 py-1">% (A) - % (B)</td>
                          <td className="border border-gray-300 px-2 py-1">32.1 - 35 = -2.9% ≈ -3%</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg border border-blue-200">
                  <h5 className="font-semibold text-blue-900 mb-2">Step 4: Ensure Proportionality (±5%)</h5>
                  <ul className="text-blue-800 space-y-1 text-sm">
                    <li>• <strong>Rule:</strong> A-B % must be within −5% to +5% for alignment</li>
                    <li>• <strong>If outside range:</strong> Adjust teaching SLT or assessment weightage</li>
                    <li>• Example: CLO3's A-B % = +5% (acceptable); CLO1's −3% (acceptable)</li>
                  </ul>
                </div>

                <div className="bg-white p-4 rounded-lg border border-blue-200">
                  <h5 className="font-semibold text-blue-900 mb-2">Step 5: Weekly SLT Breakdown</h5>
                  <ul className="text-blue-800 space-y-1 text-sm">
                    <li>• <strong>Teaching SLT:</strong> Allocate hours weekly to CLOs (e.g., Week 1: 4 hours for CLO1)</li>
                    <li>• <strong>Assessment SLT:</strong> Allocate by task deadlines (e.g., Assessment 1 due Week 3 = 8 hours)</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Alignment Rules */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-semibold text-green-900 mb-3">Alignment Rules</h4>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
                <div>
                  <h5 className="font-semibold text-green-800 mb-2">Constructive Alignment:</h5>
                  <ul className="text-green-700 space-y-1">
                    <li>• CLO: "Analyze ethical dilemmas in nursing"</li>
                    <li>• Assessment: Case study analysis (verb: "analyze")</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-semibold text-green-800 mb-2">Formative Alignment:</h5>
                  <p className="text-green-700">Ungraded peer feedback on draft case studies → Improves final summative submission</p>
                </div>
              </div>
            </div>

            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h4 className="font-semibold text-yellow-900 mb-2">Critical Success Factors:</h4>
              <ul className="text-yellow-800 space-y-1 text-sm">
                <li>• <strong>Inputs:</strong> Credit hours → Total SLT → Split teaching/assessment SLT → Map to CLOs</li>
                <li>• <strong>Outputs:</strong> Verify A-B % within ±5%. Adjust configuration if not aligned</li>
                <li>• <strong>Critical:</strong> Weekly SLT scheduling must reflect CLO emphasis and assessment deadlines</li>
                <li>• This ensures fair weightage, OBE compliance, and transparent workload distribution</li>
              </ul>
            </div>
          </motion.div>
        )}

        {/* Main SLT Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-4 py-2 text-left">CLO</th>
                <th className="border border-gray-300 px-4 py-2 text-left">SLT (Teaching)</th>
                <th className="border border-gray-300 px-4 py-2 text-left">% (A)</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Weightage</th>
                <th className="border border-gray-300 px-4 py-2 text-left">SLT (Assessment)</th>
                <th className="border border-gray-300 px-4 py-2 text-left">% (B)</th>
                <th className="border border-gray-300 px-4 py-2 text-left">A-B (%)</th>
              </tr>
            </thead>
            <tbody>
              {sltData.map((item, index) => {
                const alignment = getAlignmentStatus(item.difference);
                return (
                  <tr key={index}>
                    <td className="border border-gray-300 px-4 py-2 font-medium">{item.clo}</td>
                    <td className="border border-gray-300 px-4 py-2">{item.sltTeaching} hrs</td>
                    <td className="border border-gray-300 px-4 py-2">{item.percentageA}%</td>
                    <td className="border border-gray-300 px-4 py-2">{item.weightage}%</td>
                    <td className="border border-gray-300 px-4 py-2">{item.sltAssessment} hrs</td>
                    <td className="border border-gray-300 px-4 py-2">{item.percentageB}%</td>
                    <td className={`border border-gray-300 px-4 py-2 font-medium ${alignment.color}`}>
                      {item.difference > 0 ? '+' : ''}{item.difference}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-gray-100">
                <td className="border border-gray-300 px-4 py-2 font-bold">Total</td>
                <td className="border border-gray-300 px-4 py-2 font-bold">{totalTeachingSLT} hrs</td>
                <td className="border border-gray-300 px-4 py-2 font-bold">100%</td>
                <td className="border border-gray-300 px-4 py-2 font-bold">
                  {sltData.reduce((sum, item) => sum + item.weightage, 0)}%
                </td>
                <td className="border border-gray-300 px-4 py-2 font-bold">{totalAssessmentSLT} hrs</td>
                <td className="border border-gray-300 px-4 py-2"></td>
                <td className="border border-gray-300 px-4 py-2"></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* F2F vs Independent Learning Breakdown */}
        {showBreakdown && (
          <motion.div
            initial={{opacity: 0, height: 0}}
            animate={{opacity: 1, height: 'auto'}}
            className="mt-8"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">F2F vs Independent Learning Breakdown</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-green-50">
                    <th className="border border-gray-300 px-4 py-2 text-left">CLO</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">F2F Teaching</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">F2F Assessment</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Independent Teaching</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Independent Assessment</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Total CLO SLT</th>
                  </tr>
                </thead>
                <tbody>
                  {sltData.map((item, index) => (
                    <tr key={index}>
                      <td className="border border-gray-300 px-4 py-2 font-medium">{item.clo}</td>
                      <td className="border border-gray-300 px-4 py-2">{item.f2fTeaching} hrs</td>
                      <td className="border border-gray-300 px-4 py-2">{item.f2fAssessment} hrs</td>
                      <td className="border border-gray-300 px-4 py-2">{item.independentTeaching} hrs</td>
                      <td className="border border-gray-300 px-4 py-2">{item.independentAssessment} hrs</td>
                      <td className="border border-gray-300 px-4 py-2 font-medium">
                        {item.sltTeaching + item.sltAssessment} hrs
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-green-100">
                    <td className="border border-gray-300 px-4 py-2 font-bold">Total</td>
                    <td className="border border-gray-300 px-4 py-2 font-bold">
                      {sltData.reduce((sum, item) => sum + item.f2fTeaching, 0)} hrs
                    </td>
                    <td className="border border-gray-300 px-4 py-2 font-bold">
                      {sltData.reduce((sum, item) => sum + item.f2fAssessment, 0)} hrs
                    </td>
                    <td className="border border-gray-300 px-4 py-2 font-bold">
                      {sltData.reduce((sum, item) => sum + item.independentTeaching, 0)} hrs
                    </td>
                    <td className="border border-gray-300 px-4 py-2 font-bold">
                      {sltData.reduce((sum, item) => sum + item.independentAssessment, 0)} hrs
                    </td>
                    <td className="border border-gray-300 px-4 py-2 font-bold">{totalCourseSLT} hrs</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* F2F vs Independent Learning Summary */}
            <div className="mt-6 bg-green-50 p-4 border border-green-200 rounded-lg">
              <h4 className="font-semibold text-green-900 mb-3">F2F vs Independent Learning Summary</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white p-3 rounded-lg border border-green-200">
                  <h5 className="font-medium text-green-800 text-sm">Total F2F Hours</h5>
                  <p className="text-lg font-semibold text-green-900">
                    {breakdownConfig.f2fTeaching + breakdownConfig.f2fAssessment} hrs
                  </p>
                  <p className="text-xs text-green-600">
                    {Math.round(((breakdownConfig.f2fTeaching + breakdownConfig.f2fAssessment) / courseSLTConfig.totalCourseSLT) * 100)}% of total
                  </p>
                </div>
                <div className="bg-white p-3 rounded-lg border border-green-200">
                  <h5 className="font-medium text-green-800 text-sm">Total Independent Hours</h5>
                  <p className="text-lg font-semibold text-green-900">
                    {breakdownConfig.independentTeaching + breakdownConfig.independentAssessment} hrs
                  </p>
                  <p className="text-xs text-green-600">
                    {Math.round(((breakdownConfig.independentTeaching + breakdownConfig.independentAssessment) / courseSLTConfig.totalCourseSLT) * 100)}% of total
                  </p>
                </div>
                <div className="bg-white p-3 rounded-lg border border-green-200">
                  <h5 className="font-medium text-green-800 text-sm">F2F Learning %</h5>
                  <p className="text-lg font-semibold text-green-900">
                    {courseSLTConfig.totalCourseSLT > 0 
                      ? Math.round(((breakdownConfig.f2fTeaching + breakdownConfig.f2fAssessment) / courseSLTConfig.totalCourseSLT) * 100)
                      : 0}%
                  </p>
                </div>
                <div className="bg-white p-3 rounded-lg border border-green-200">
                  <h5 className="font-medium text-green-800 text-sm">Independent Learning %</h5>
                  <p className="text-lg font-semibold text-green-900">
                    {courseSLTConfig.totalCourseSLT > 0 
                      ? Math.round(((breakdownConfig.independentTeaching + breakdownConfig.independentAssessment) / courseSLTConfig.totalCourseSLT) * 100)
                      : 0}%
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Assessment Alignment Analysis */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">SLT-Assessment Alignment Analysis:</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="bg-white p-3 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-green-700">Well Aligned</h4>
              <p className="text-green-600">CLOs with A-B% within ±5.0% range</p>
              <p className="text-lg font-bold text-green-600">
                {sltData.filter(item => Math.abs(item.difference) <= 5).length}/{sltData.length}
              </p>
            </div>
            <div className="bg-white p-3 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-yellow-700">Needs Attention</h4>
              <p className="text-yellow-600">CLOs with A-B% between ±5.1% to ±10.0%</p>
              <p className="text-lg font-bold text-yellow-600">
                {sltData.filter(item => Math.abs(item.difference) > 5 && Math.abs(item.difference) <= 10).length}/{sltData.length}
              </p>
            </div>
            <div className="bg-white p-3 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-red-700">Requires Adjustment</h4>
              <p className="text-red-600">CLOs with A-B% exceeding ±10.0%</p>
              <p className="text-lg font-bold text-red-600">
                {sltData.filter(item => Math.abs(item.difference) > 10).length}/{sltData.length}
              </p>
            </div>
          </div>
        </div>

        {/* Recommendations */}
        {sltData.some(item => Math.abs(item.difference) > 5) && (
          <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <h4 className="font-semibold text-orange-900 mb-2">⚠️ Recommendations for Improvement:</h4>
            <ul className="text-orange-800 space-y-2">
              {sltData.filter(item => Math.abs(item.difference) > 5).map((item, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <span className="font-medium text-orange-900">{item.clo}:</span>
                  <span>
                    {item.difference > 0 
                      ? `Consider reducing teaching SLT hours or increasing assessment weightage (currently ${item.difference.toFixed(1)}% over-allocated in teaching)`
                      : `Consider increasing teaching SLT hours or reducing assessment weightage (currently ${Math.abs(item.difference).toFixed(1)}% under-allocated in teaching)`
                    }
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default SLTWeightage;